/** QuickRouter — Schéma : connectivité, netlist dérivée, opérations et contrôle électrique (ERC).
 *
 * La netlist du projet (project.nets) est CALCULÉE à partir du schéma :
 *  - un fil relie ce qui touche ses segments (bout de broche, extrémité d'un autre fil, étiquette) ;
 *    deux fils qui se croisent sans extrémité commune ne sont PAS reliés ;
 *  - une étiquette (net, alimentation, masse) nomme ce qu'elle touche ; deux étiquettes de même
 *    nom relient leurs groupes, où qu'elles soient (étiquettes globales) ;
 *  - une étiquette « attachée » à une broche (at: {comp, pin}) la nomme sans fil — c'est la forme
 *    compacte que produit l'assistant et la migration des projets antérieurs au schéma.
 * Les propriétés d'un net (classe, largeur) sont conservées par son nom d'un calcul à l'autre.
 */
import { newId, LabelSchema, WireSchema } from './schema.js';
import { symbolFor, placedPins, placedBox, G } from './symbols.js';
import { guessKind } from './ops-kind.js';

const EPS = 0.01;
const r3 = (v) => Math.round(v * 1000) / 1000;
const snap = (v) => r3(Math.round(v / G) * G);

export function emptySchematic() {
  return { v: 1, symbols: [], wires: [], labels: [], notes: [] };
}

// ── Géométrie ──────────────────────────────────────────────────

const same = (a, b) => Math.abs(a[0] - b[0]) < EPS && Math.abs(a[1] - b[1]) < EPS;

/** Point p sur le segment [a, b] (fils orthogonaux ou obliques). */
function onSegment(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  if (l2 < EPS * EPS) return same(p, a);
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2;
  if (t < -EPS || t > 1 + EPS) return false;
  const qx = a[0] + t * dx, qy = a[1] + t * dy;
  return Math.hypot(p[0] - qx, p[1] - qy) < EPS;
}

// ── Broches placées ────────────────────────────────────────────

/** Toutes les broches du schéma : [{ comp, ref, num, name, key, pos, dir }]. */
export function schematicPins(project, lookup) {
  const sch = project.schematic ?? emptySchematic();
  const byId = new Map(project.components.map((c) => [c.id, c]));
  const out = [];
  for (const pl of sch.symbols) {
    const comp = byId.get(pl.comp);
    if (!comp) continue;
    const sym = symbolFor(lookup(comp.footprint));
    for (const p of placedPins(pl, sym)) out.push({ ...p, comp: comp.id, ref: comp.ref, key: `${comp.ref}.${p.num}` });
  }
  return out;
}

// ── Connectivité ───────────────────────────────────────────────

class DSU {
  constructor() { this.p = new Map(); }
  find(x) { if (!this.p.has(x)) this.p.set(x, x); let r = x; while (this.p.get(r) !== r) r = this.p.get(r); this.p.set(x, r); return r; }
  union(a, b) { const x = this.find(a), y = this.find(b); if (x !== y) this.p.set(x, y); }
}

/**
 * Groupes électriques du schéma : [{ names: Set, pins: Set<clé REF.NUM> }]
 * (+ conflits : groupes portant plusieurs noms).
 */
export function connectivity(project, lookup) {
  const sch = project.schematic ?? emptySchematic();
  const dsu = new DSU();
  const pins = schematicPins(project, lookup);
  const byComp = new Map(project.components.map((c) => [c.id, c]));
  const segs = [];
  for (const w of sch.wires) for (let i = 1; i < w.pts.length; i++) segs.push({ id: `w:${w.id}`, a: w.pts[i - 1], b: w.pts[i] });
  for (const w of sch.wires) for (let i = 2; i < w.pts.length; i++) dsu.union(`w:${w.id}`, `w:${w.id}`);

  // Fil ↔ fil : une extrémité (ou un sommet) de l'un sur un segment de l'autre.
  for (const w of sch.wires) {
    const ends = [w.pts[0], w.pts[w.pts.length - 1]];
    for (const s of segs) {
      if (s.id === `w:${w.id}`) continue;
      if (ends.some((e) => onSegment(e, s.a, s.b))) dsu.union(`w:${w.id}`, s.id);
    }
    for (const v of w.pts.slice(1, -1)) {
      for (const o of sch.wires) {
        if (o === w) continue;
        if (same(v, o.pts[0]) || same(v, o.pts[o.pts.length - 1])) dsu.union(`w:${w.id}`, `w:${o.id}`);
      }
    }
  }
  // Broche ↔ fil, broche ↔ broche (bouts confondus).
  for (const p of pins) {
    dsu.find(`p:${p.key}`);
    for (const s of segs) if (onSegment(p.pos, s.a, s.b)) dsu.union(`p:${p.key}`, s.id);
  }
  for (let i = 0; i < pins.length; i++) {
    for (let j = i + 1; j < pins.length; j++) if (same(pins[i].pos, pins[j].pos)) dsu.union(`p:${pins[i].key}`, `p:${pins[j].key}`);
  }
  // Étiquettes : posées (touchent un fil ou un bout de broche) ou attachées à une broche.
  const names = new Map(); // racine provisoire → noms
  const labelNodes = [];
  for (const l of sch.labels) {
    let node = null;
    if (l.at) {
      const comp = byComp.get(l.at.comp);
      if (comp) node = `p:${comp.ref}.${l.at.pin}`;
    } else if (l.pos) {
      const s = segs.find((x) => onSegment(l.pos, x.a, x.b));
      const p = pins.find((x) => same(x.pos, l.pos));
      node = s?.id ?? (p ? `p:${p.key}` : null);
    }
    if (!node) continue;
    dsu.find(node);
    labelNodes.push([node, l.name, l.id]);
  }
  // Même nom = même net.
  const firstByName = new Map();
  for (const [node, name] of labelNodes) {
    if (firstByName.has(name)) dsu.union(node, firstByName.get(name));
    else firstByName.set(name, node);
  }
  for (const [node, name] of labelNodes) {
    const r = dsu.find(node);
    if (!names.has(r)) names.set(r, new Set());
    names.get(r).add(name);
  }
  const groups = new Map();
  const pinKeys = new Set(pins.map((p) => p.key));
  // Broches sans symbole mais étiquetées (trous de fixation reliés à la masse…).
  for (const [node] of labelNodes) if (node.startsWith('p:')) pinKeys.add(node.slice(2));
  for (const key of pinKeys) {
    const r = dsu.find(`p:${key}`);
    if (!groups.has(r)) groups.set(r, { names: names.get(r) ?? new Set(), pins: new Set() });
    groups.get(r).pins.add(key);
  }
  for (const [r, n] of names) if (!groups.has(r)) groups.set(r, { names: n, pins: new Set() });
  const list = [...groups.values()];
  // Groupe de chaque fil et de chaque étiquette (mise en évidence d'un net dans l'éditeur).
  const wires = new Map(), labels = new Map();
  for (const w of sch.wires) { const g = groups.get(dsu.find(`w:${w.id}`)); if (g) wires.set(w.id, g); }
  for (const [node, , id] of labelNodes) { const g = groups.get(dsu.find(node)); if (g) labels.set(id, g); }
  return { groups: list, conflicts: list.filter((g) => g.names.size > 1), wires, labels };
}

/** Nom d'un net : étiquette (la première par ordre alphabétique en cas de conflit) ou « N-REF-NUM ». */
function netName(g) {
  if (g.names.size) return [...g.names].sort()[0];
  const first = [...g.pins].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))[0];
  return `N-${first.replace('.', '-')}`;
}

/** Recalcule project.nets depuis le schéma (classe et largeur conservées par nom). */
export function syncNets(project, lookup) {
  if (!project.schematic) return project.nets;
  const prev = new Map(project.nets.map((n) => [n.name, n]));
  const { groups } = connectivity(project, lookup);
  const nets = [];
  const seen = new Set();
  for (const g of groups) {
    if (g.pins.size < 2 && !g.names.size) continue;
    const name = netName(g);
    if (seen.has(name)) continue;
    seen.add(name);
    const old = prev.get(name);
    nets.push({
      id: old?.id ?? newId('n'),
      name,
      pins: [...g.pins].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true })),
      kind: old?.kind ?? guessKind(name),
      ...(old?.width ? { width: old.width } : {}),
    });
  }
  nets.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true }));
  project.nets = nets;
  return nets;
}

// ── Placement des symboles ─────────────────────────────────────

/** Emplacement libre pour un nouveau symbole : à droite de l'existant, en colonnes. */
export function freeSpot(project, lookup, sym) {
  const sch = project.schematic;
  const byId = new Map(project.components.map((c) => [c.id, c]));
  const boxes = sch.symbols.map((pl) => {
    const c = byId.get(pl.comp);
    const s = c && symbolFor(lookup(c.footprint));
    return s ? placedBox(pl, s) : null;
  }).filter(Boolean);
  const w = sym.box[1][0] - sym.box[0][0], h = sym.box[1][1] - sym.box[0][1];
  const COL = 40, ROW_TOP = 200;
  for (let col = 0; col < 40; col++) {
    let y = ROW_TOP;
    for (;;) {
      const x = 20 + col * COL;
      const cand = [x + sym.box[0][0], y - h - 5, x + sym.box[0][0] + w + 6, y + 5];
      if (y - h < 10) break;
      const hit = boxes.some((b) => !(b[2] < cand[0] || cand[2] < b[0] || b[3] < cand[1] || cand[3] < b[1]));
      if (!hit && w + 6 <= COL * 2) return [snap(x), snap(y - sym.box[1][1])];
      y -= G * 2;
    }
  }
  return [snap(20 + boxes.length * 3), 20];
}

/** Ajoute le symbole d'un composant (s'il en a un et n'en a pas déjà). */
export function addSymbol(project, lookup, comp, pos) {
  if (!project.schematic) project.schematic = emptySchematic();
  if (project.schematic.symbols.some((s) => s.comp === comp.id)) return null;
  const sym = symbolFor(lookup(comp.footprint));
  if (!sym) return null;
  const pl = { comp: comp.id, pos: pos ? pos.map(snap) : freeSpot(project, lookup, sym), rot: 0, mirror: false };
  project.schematic.symbols.push(pl);
  return pl;
}

export function moveSymbol(project, compId, patch) {
  const pl = project.schematic?.symbols.find((s) => s.comp === compId);
  if (!pl) throw new Error('ce composant n\'a pas de symbole');
  if (patch.pos) pl.pos = patch.pos.map(snap);
  if (patch.rot !== undefined) pl.rot = (((Math.round(patch.rot / 90) * 90) % 360) + 360) % 360;
  if (patch.mirror !== undefined) pl.mirror = !!patch.mirror;
  return pl;
}

/** Retire symboles et étiquettes attachées des composants supprimés. */
export function forgetComponents(project, compIds) {
  if (!project.schematic) return;
  const gone = new Set(compIds);
  project.schematic.symbols = project.schematic.symbols.filter((s) => !gone.has(s.comp));
  project.schematic.labels = project.schematic.labels.filter((l) => !l.at || !gone.has(l.at.comp));
}

// ── Fils et étiquettes ─────────────────────────────────────────

export function addWire(project, pts) {
  const clean = pts.map((p) => p.map(r3)).filter((p, i, a) => i === 0 || !same(p, a[i - 1]));
  if (clean.length < 2) throw new Error('un fil a au moins deux points distincts');
  const w = WireSchema.parse({ id: newId('w'), pts: clean });
  project.schematic.wires.push(w);
  return w;
}

export function addLabel(project, spec) {
  const l = LabelSchema.parse({ id: newId('l'), kind: labelKind(spec.name), ...spec });
  project.schematic.labels.push(l);
  return l;
}

/** Masse et alimentations dessinées en symbole d'alimentation. */
export function labelKind(name) {
  if (/^(gnd|agnd|pgnd|0v|masse)$/i.test(name)) return 'gnd';
  return guessKind(name) === 'power' ? 'power' : 'net';
}

/** Nomme une broche (étiquette attachée) ; une broche ne porte qu'une étiquette attachée. */
export function labelPin(project, comp, pin, name) {
  project.schematic.labels = project.schematic.labels.filter((l) => !(l.at && l.at.comp === comp.id && l.at.pin === pin));
  if (name) addLabel(project, { name, at: { comp: comp.id, pin } });
}

export function deleteSchItems(project, ids) {
  const set = new Set(ids);
  const sch = project.schematic;
  const n = sch.wires.length + sch.labels.length + sch.notes.length;
  sch.wires = sch.wires.filter((w) => !set.has(w.id));
  sch.labels = sch.labels.filter((l) => !set.has(l.id));
  sch.notes = sch.notes.filter((t) => !set.has(t.id));
  return n - (sch.wires.length + sch.labels.length + sch.notes.length);
}

// ── Migration des projets antérieurs au schéma ─────────────────

/** Crée le schéma d'un projet qui n'en a pas : symboles rangés, broches nommées par leur net. */
export function ensureSchematic(project, lookup) {
  if (project.schematic) return false;
  project.schematic = emptySchematic();
  for (const c of project.components) addSymbol(project, lookup, c);
  const byRef = new Map(project.components.map((c) => [c.ref, c]));
  for (const n of project.nets) {
    for (const key of n.pins) {
      const dot = key.lastIndexOf('.');
      const comp = byRef.get(key.slice(0, dot));
      if (comp) addLabel(project, { name: n.name, at: { comp: comp.id, pin: key.slice(dot + 1) } });
    }
  }
  syncNets(project, lookup);
  return true;
}

// ── ERC ────────────────────────────────────────────────────────

const POWER_PIN = /^(vcc|vdd|vin|3v3|5v|\+?\d+v\d*|vbus|vbat|vout|gnd|vss|avdd|dvdd)$/i;

/** Contrôle électrique du schéma. */
export function runErc(project, lookup) {
  const errors = [], warnings = [];
  const { groups, conflicts } = connectivity(project, lookup);
  for (const g of conflicts) errors.push({ type: 'conflict', message: `Nets court-circuités : ${[...g.names].join(' et ')} sont reliés (${[...g.pins].slice(0, 6).join(', ')})` });
  const netOf = new Map();
  for (const g of groups) for (const k of g.pins) netOf.set(k, g);
  for (const g of groups) {
    if (g.names.size && g.pins.size === 1) warnings.push({ type: 'single', message: `Net ${[...g.names][0]} : une seule broche (${[...g.pins][0]})` });
    if (g.names.size && g.pins.size === 0) warnings.push({ type: 'orphan-label', message: `Étiquette ${[...g.names][0]} reliée à aucune broche` });
  }
  const byId = new Map(project.components.map((c) => [c.id, c]));
  const placed = new Set((project.schematic?.symbols ?? []).map((s) => s.comp));
  for (const c of project.components) {
    const fp = lookup(c.footprint);
    if (!fp) continue;
    if (symbolFor(fp) && !placed.has(c.id)) warnings.push({ type: 'no-symbol', message: `${c.ref} n'a pas de symbole dans le schéma` });
  }
  for (const p of schematicPins(project, lookup)) {
    const g = netOf.get(p.key);
    const connected = g && (g.pins.size > 1 || g.names.size);
    if (!connected && POWER_PIN.test(p.name || '')) errors.push({ type: 'power-pin', message: `${p.key} (${p.name}) : broche d'alimentation non reliée`, at: p.pos });
    if (!connected && byId.get(p.comp) && !byId.get(p.comp).locked && /^(U|Q|D)/.test(p.ref) && !/^(NC|N\/C)$/i.test(p.name || '')) {
      warnings.push({ type: 'unconnected', message: `${p.key}${p.name ? ` (${p.name})` : ''} non reliée`, at: p.pos });
    }
  }
  // Fils dont une extrémité ne touche rien.
  const pins = schematicPins(project, lookup);
  const sch = project.schematic ?? emptySchematic();
  for (const w of sch.wires) {
    for (const e of [w.pts[0], w.pts[w.pts.length - 1]]) {
      const onPin = pins.some((p) => same(p.pos, e));
      const onWire = sch.wires.some((o) => o !== w && o.pts.some((q, i) => i > 0 && onSegment(e, o.pts[i - 1], q)));
      const onLabel = sch.labels.some((l) => l.pos && same(l.pos, e));
      if (!onPin && !onWire && !onLabel) { warnings.push({ type: 'dangling', message: 'Extrémité de fil en l\'air', at: e }); }
    }
  }
  return { ok: errors.length === 0, errors: errors.slice(0, 200), warnings: warnings.slice(0, 300) };
}
